import { ASSIGNABLE_WELLSPRING_ROLES, type AssignableWellspringRole } from './types/users.ts';

/**
 * Wellspring role hierarchy for the Wellspring Access page. A caller may only
 * manage users whose current role is below their own, and may only assign roles
 * below their own — so adding an Admin still takes a SuperAdmin.
 */

const ROLE_RANK: Record<AssignableWellspringRole, number> = {
  SuperAdmin: 3,
  Admin: 2,
  Support: 1,
};

export function isAssignableWellspringRole(value: string): value is AssignableWellspringRole {
  return (ASSIGNABLE_WELLSPRING_ROLES as readonly string[]).includes(value);
}

/** Rank of a role; null (implicit Viewer) ranks lowest. */
export function wellspringRoleRank(role: AssignableWellspringRole | null): number {
  return role ? ROLE_RANK[role] : 0;
}

/** Highest Wellspring role among the caller's (prefix-stripped) groups. */
export function highestWellspringRole(groups: readonly string[]): AssignableWellspringRole | null {
  let best: AssignableWellspringRole | null = null;
  for (const g of groups) {
    if (isAssignableWellspringRole(g) && wellspringRoleRank(g) > wellspringRoleRank(best)) best = g;
  }
  return best;
}

/**
 * Whether a caller holding `callerGroups` may move a user from `currentRole`
 * to `newRole`. Both must rank strictly below the caller's own role.
 */
export function canManageWellspringRole(
  callerGroups: readonly string[],
  currentRole: AssignableWellspringRole | null,
  newRole: AssignableWellspringRole | null,
): boolean {
  const caller = wellspringRoleRank(highestWellspringRole(callerGroups));
  return wellspringRoleRank(currentRole) < caller && wellspringRoleRank(newRole) < caller;
}
